"use client";

import * as React from "react";
import { getPremiumCompletePassSalesStatus } from "@/lib/pass-sales";
import type { NavSubLink } from "@/lib/nav-sections";

/**
 * 시각(시간)에 따라 켜지고 꺼지는 네비게이션 뱃지 주입 SSOT (JCG-3023 후속).
 *
 * `nav-sections.ts` 는 정적 모듈이라 "곧 마감"처럼 특정 시각이 지나면 거짓이 되는 뱃지를 둘 수 없다
 * (마감 다음날부터 사용자에게 거짓 정보). 그래서 소비 시점에 판매 일정 SSOT(`pass-sales.ts`)를 읽어
 * 덧입힌다. `Menu.tsx`(상단 NAV·모바일 드롭다운)와 `article-shell/IconRail.tsx`(사이드바 드로어·
 * 플라이아웃)가 이 모듈을 공유하므로 두 surface 의 마감 신호가 항상 같은 시각에 켜지고 꺼진다.
 *
 * 표현(pill/도트 클래스)은 `@/components/nav-badges` 담당 — 여기엔 로직만 둔다.
 */

/**
 * 판매 종료 D-N 이내부터 "곧 마감" 신호를 노출한다.
 *
 * **`pass-sales.ts` 의 `PREMIUM_COMPLETE_SALES_CUTOFF_DAYS` 와 독립이다. 값이 같아져도 묶지 말 것.**
 * 저건 *언제 파는 걸 멈추나*(앵커: 시험일), 이건 *멈추기 얼마 전부터 경고하나*(앵커: 판매 종료일).
 * 앵커가 다르므로 컷오프가 바뀌어도 이 값은 그대로 "판매 종료 3일 전"을 정확히 유지한다 —
 * D-3→D-1 전환 때 이 파일 로직을 안 고쳐도 됐던 게 그 증거다.
 * 반례(2026-09-17 JCG-3598 로 제거된 PdfUpdatePopup 의 마감 카드): **뜨는 시점**은 이 상수(판매종료 D-3),
 * **말하는 내용**은 저 상수("시험 하루 전부터 판매 안 함")로 둘 다 동시에 참이다. 묶으면 표현 불가.
 */
const PASS_SALES_CLOSING_SOON_DAYS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Premium·Complete 판매 종료 임박 여부.
 *
 * 판매 일정 SSOT(`pass-sales.ts`)의 `salesEndAtISO` 에서 파생하므로 판매 종료 시점에
 * **자동으로 꺼진다**. 날짜를 하드코딩하면 마감 다음날부터 거짓 정보가 되므로 금지.
 */
export function isPassSalesClosingSoon(now: number): boolean {
  const status = getPremiumCompletePassSalesStatus(now);
  if (status.isClosed || !status.season) return false;

  const remainingMs = new Date(status.season.salesEndAtISO).getTime() - now;
  return remainingMs > 0 && remainingMs <= PASS_SALES_CLOSING_SOON_DAYS * DAY_MS;
}

/** 외부 스토어가 아니라 1회성 스냅샷이므로 구독하지 않는다(경계는 다음 내비게이션에서 재평가). */
const subscribeNoop = () => () => {};

/**
 * 시각 의존 값이라 SSR 렌더 시점과 hydration 시점이 마감 경계를 사이에 두고 갈릴 수 있다.
 * `useSyncExternalStore` 의 server 스냅샷을 false 로 고정해 초기 렌더/hydration 을 항상 일치시키고,
 * hydration 직후 client 스냅샷으로 교체한다(`Menu.tsx` `AuthSection` 마운트 가드와 같은 목적의
 * hydration-safe 패턴이며, effect 안 setState 없이 처리된다).
 *
 * 이 뱃지는 닫힌 드롭다운·플라이아웃 안에만 있어 초기 페인트에 보이지 않으므로 교체에 따른 CLS 가 없다.
 * 반대로 **항상 보이는 자리(트리거·접힌 레일 아이콘)에는 이 신호를 쓰지 말 것** — 팝인이 그대로 보인다.
 */
export function usePassSalesClosingSoon(): boolean {
  return React.useSyncExternalStore(
    subscribeNoop,
    () => isPassSalesClosingSoon(Date.now()),
    () => false,
  );
}

/**
 * 시각 파생 뱃지를 소비 시점에 주입한다. `NAV_SECTIONS`(정적 모듈)에는 마감처럼 시간이 지나면
 * 거짓이 되는 뱃지를 넣을 수 없으므로 여기서 덧입힌다.
 *
 * **우선순위: "곧 마감"(시각 파생) > 정적 badge.** 마감 임박은 기한이 있는 유일한 신호라
 * NEW/UPDATE 같은 정적 뱃지를 의도적으로 덮어쓴다. 즉 `/membership` 항목에 정적 badge 를 넣으면
 * 마감 임박 구간에는 그 badge 가 보이지 않는다 — 뱃지 자리가 하나뿐이라 둘 중 하나를 골라야 하고,
 * 그 구간에서 더 급한 정보는 마감이기 때문이다. 날짜에 따라 뱃지가 달라 보이는 게 정상 동작이다.
 */
export function withTimedNavBadge(
  item: NavSubLink,
  passSalesClosingSoon: boolean,
): NavSubLink {
  if (passSalesClosingSoon && item.href === "/membership") {
    // 위 우선순위 계약에 따른 의도적 덮어쓰기(item.badge 가 있어도 마감이 이긴다).
    return { ...item, badge: "곧 마감" };
  }
  return item;
}
